import React, { useMemo, useState } from 'react';
import { Box, FormPreloader, RawButton, SearchInput, Text } from '@common-fe/common-fe';

import FieldLevelAccessGroup from '@/modules/role/FieldLevelAccess/FieldLevelAccessGroup';
import { FieldLevelAccessSetupProperties, IFieldLevelAccessProperties } from '@/modules/role/role.types';

interface Props extends FieldLevelAccessSetupProperties {
  isLoading?: boolean;
  isSearchLess?: boolean;
  isEditMode?: boolean;
}
const FieldLevelAccessPropertiesConfirmSetup: React.FC<Props> = ({
  isLoading = false,
  isSearchLess,
  properties,
  overridableProperties,
  isEditMode,
}) => {
  const [searchString, setSearchString] = useState('');
  const [expanded, setExpanded] = useState<boolean>(true);

  const filteredProperties = useMemo(() => {
    const result = {
      properties,
      overridableProperties,
    };

    if (!searchString || !searchString.trim()) {
      return result;
    }

    if (properties) {
      const filteredProperties = Object.keys(properties).reduce((acc, key) => {
        const formattedKey = key.split('_').join(' ').toLocaleLowerCase();
        if (formattedKey.includes(searchString.toLowerCase())) {
          acc[key] = properties[key];
        }
        return acc;
      }, {} as IFieldLevelAccessProperties);

      result.properties = filteredProperties;
    }

    if (overridableProperties) {
      const filteredOverridableProperties = Object.keys(overridableProperties).reduce((acc, key) => {
        const formattedKey = key.split('_').join(' ').toLocaleLowerCase();
        if (formattedKey.includes(searchString.toLowerCase())) {
          acc[key] = overridableProperties[key];
        }
        return acc;
      }, {} as IFieldLevelAccessProperties);

      result.overridableProperties = filteredOverridableProperties;
    }

    return result;
  }, [overridableProperties, properties, searchString]);

  const showProperties = useMemo(() => {
    return filteredProperties?.properties && Object.keys(filteredProperties.properties).length > 0;
  }, [filteredProperties]);

  const showOverridableProperties = useMemo(() => {
    return (
      filteredProperties?.overridableProperties &&
      Object.keys(filteredProperties.overridableProperties).length > 0
    );
  }, [filteredProperties]);

  return (
    <Box direction="column">
      {!isSearchLess && (
        <Box direction="row" height="40px" justify="between" align="center">
          <Text size="xxlarge" weight={400}>
            Permissions
          </Text>
          <Box width="320px">
            <SearchInput value={searchString} placeholder="Search" onChange={setSearchString} />
          </Box>
        </Box>
      )}

      <Box border={{ side: 'bottom', size: 'small', color: 'border1' }} margin={{ vertical: 'm' }} />

      <Box direction="column">
        {isLoading ? (
          <FormPreloader count={4} />
        ) : (
          <Box direction="column">
            <RawButton onClick={() => setExpanded(!expanded)}>
              <Text color="textAccent" weight={400}>
                {expanded ? 'Collapse all' : 'Expand all'}
              </Text>
            </RawButton>
            {showProperties && (
              <FieldLevelAccessGroup
                testId="normal-properties"
                defaultExpanded={expanded}
                name="Normal Organization Properties"
                items={filteredProperties.properties as IFieldLevelAccessProperties}
                withTopBorder={false}
                isEditMode={isEditMode}
              />
            )}

            {showProperties && showOverridableProperties && (
              <Box border={{ side: 'bottom', size: 'small', color: 'border1' }} margin={{ vertical: 'm' }} />
            )}

            {showOverridableProperties && (
              <FieldLevelAccessGroup
                isOverridableProperties
                testId="override-properties"
                defaultExpanded={expanded}
                name="Overridable Organization Properties"
                items={filteredProperties.overridableProperties as IFieldLevelAccessProperties}
                withTopBorder={false}
                isEditMode={isEditMode}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FieldLevelAccessPropertiesConfirmSetup;
